import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-location';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AppointmentReminders as AppointmentRemindersGraphic } from '@frontend/empty-states#svg';
import { Trans, useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text, TextLink } from '@frontend/design-system';
import { URLs } from '../../../../constants';
import { findExistingTimePeriodKey } from '../../../../hooks';
import { Insights } from '../../../homepage-analytics';
import { InfoTipPopover } from '../../../info-tip-popover';
import { useARRoiShallowStore, useFetchARROIOOverview } from '../../appointment-reminders/hooks';
import { BenchmarkComparisonText, ChangeProps, nsrTimePeriods } from '../components';
import { ROIFilters, useFetchNSRROITimeline, useNSRRoiShallowStore } from '../hooks';
import { NSRRoiTimelineChartBase } from './nsr-roi-timeline-chart-base';
import { CommonChartProps } from './types';

interface Props extends CommonChartProps {
  filters?: ROIFilters;
  isOnDashboard?: boolean;
}

const AppointmentReminders = ({ diffFromBenchmark = 0, diffFromLastPeriod = 0 }: ChangeProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { navigate: settingsNavigate } = useSettingsNavigate();

  const hasDecreased = diffFromLastPeriod < 0;
  const isLower = diffFromBenchmark < 0;
  const isPositiveImpact = (hasDecreased && isLower) || (isLower && !diffFromLastPeriod);

  const { filters } = useNSRRoiShallowStore('filters');
  const { setDoNotOverrideFiltersOnLoad: setDoNotOverrideARFiltersOnLoad, setFilters: setARFilters } =
    useARRoiShallowStore('setDoNotOverrideFiltersOnLoad', 'setFilters');

  const handleNavigate = useCallback(() => {
    if (!filters) {
      return;
    }

    if (isPositiveImpact) {
      setDoNotOverrideARFiltersOnLoad(true);
      setARFilters(filters);
      navigate({
        to: URLs.ROI_APPOINTMENT_REMINDERS,
      });
    } else {
      settingsNavigate({ to: '/messaging/auto-messages' });
    }
  }, [isPositiveImpact, filters]);

  return (
    <div css={styles.apptsWrapper}>
      <AppointmentRemindersGraphic className='graphic' />
      <div className='content'>
        <Text className='message'>
          {isPositiveImpact ? (
            <Trans
              t={t}
              values={{
                diffFromBenchmark: formatters.appendPercent(Math.abs(diffFromBenchmark)),
              }}
            >
              Appointment Reminders have reduced your overall no-show rate by <strong>{{ diffFromBenchmark }}</strong>{' '}
              compared to the benchmark
            </Trans>
          ) : (
            t('Utilize appointment reminders to help lower your no-show rate.')
          )}
        </Text>
        <TextLink onClick={handleNavigate} weight='bold'>
          {isPositiveImpact ? t('Discover the Impact') : t('Optimize Your Reminders')}
        </TextLink>
      </div>
    </div>
  );
};

export const NSRRoiBenchmarkTimelineChart = ({ filters: propsFilters, isOnDashboard, onLoading, ...rest }: Props) => {
  const { t } = useTranslation('analytics');
  const { filters } = useNSRRoiShallowStore('filters');

  const nsrRoiTimeline = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType: NoShowRateByType.WEAVE_AVERAGE,
    },
    filters: propsFilters || filters,
  });

  const { aggregated, isLoading: isLoadingAggregates } = useFetchARROIOOverview({
    fetchNoShowRate: false,
    filters: propsFilters || filters,
    isEnabled: isOnDashboard,
    roiType: RoiType.WEAVE,
  });

  const differenceFromBenchmark = Math.round(nsrRoiTimeline.rawData?.data?.rateDifference ?? 0);
  const differenceFromLastPeriod = Math.round(nsrRoiTimeline.rawData?.data?.previousDateRangeDifference ?? 0);

  useEffect(() => {
    onLoading?.('benchmarkChart', nsrRoiTimeline?.isLoading);
  }, [nsrRoiTimeline?.isLoading, onLoading]);

  const detailsProps = useMemo(() => {
    if (isOnDashboard || nsrRoiTimeline?.isLoading) {
      return {};
    } else {
      return {
        afterChart: (
          <AppointmentReminders
            diffFromBenchmark={differenceFromBenchmark}
            diffFromLastPeriod={differenceFromLastPeriod}
          />
        ),
        beforeChart: (
          <BenchmarkComparisonText
            diffFromBenchmark={differenceFromBenchmark}
            diffFromLastPeriod={differenceFromLastPeriod}
            timePeriod={
              filters?.startDate && filters?.endDate
                ? findExistingTimePeriodKey(filters.startDate, filters.endDate, nsrTimePeriods) ?? undefined
                : undefined
            }
          />
        ),
      };
    }
  }, [
    differenceFromBenchmark,
    differenceFromLastPeriod,
    filters?.endDate,
    filters?.startDate,
    isOnDashboard,
    nsrRoiTimeline?.isLoading,
  ]);

  const tooltipBottomContentRenderer = useCallback(
    (data: Record<string, number>) => {
      const difference = Math.round(data?.practice ?? 0) - Math.round(data?.benchmark ?? 0);

      return (
        <Text color='subdued' size='small' style={{ maxWidth: '230px' }}>
          {difference < 0 ? (
            <Trans
              t={t}
              values={{
                difference: formatters.appendPercent(Math.abs(difference)),
              }}
            >
              Your practice has a <strong style={{ color: theme.colors.success50 }}>{{ difference }}</strong> lower
              no-show rate than the benchmark.
            </Trans>
          ) : difference > 0 ? (
            <Trans
              t={t}
              values={{
                difference: formatters.appendPercent(Math.abs(difference)),
              }}
            >
              Your practice has a <strong style={{ color: theme.colors.critical50 }}>{{ difference }}</strong> higher
              no-show rate than the benchmark.
            </Trans>
          ) : (
            <Trans
              t={t}
              values={{
                difference: formatters.appendPercent(Math.abs(difference)),
              }}
            >
              Your practice has a same no-show rate as the benchmark.
            </Trans>
          )}
        </Text>
      );
    },
    [t]
  );

  return (
    <NSRRoiTimelineChartBase
      {...rest}
      {...nsrRoiTimeline}
      {...detailsProps}
      bottomContentRenderer={
        isOnDashboard ? (
          <Insights title={t('No-Show Rate')} value={formatters.appendPercent(aggregated.noShowRatePercentage)} />
        ) : null
      }
      infoTip={
        <InfoTipPopover>{t('Track no-show performance for your location with historical trends')}</InfoTipPopover>
      }
      isLoading={nsrRoiTimeline.isLoading || isLoadingAggregates}
      isOnDashboard={isOnDashboard}
      // TODO :: uncomment it when multi is ready to land
      // showCompareMulti={isOnDashboard && ((propsFilters ?? filters)?.locationIds?.length || 0) > 1}
      title={t('No-Show Rate')}
      tooltipBottomContentRenderer={tooltipBottomContentRenderer}
      trackingIdBase='no-show-rate-benchmark'
    />
  );
};

const styles = {
  apptsWrapper: css`
    align-items: center;
    background-color: ${theme.colors.tangerine5};
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.tangerine40};
    display: flex;
    padding: ${theme.spacing(2)};
    position: relative;

    .message {
      margin-bottom: ${theme.spacing(1)};
      max-width: calc(100% - 200px);
    }

    > div:not(.content),
    .graphic {
      height: 100%;
      max-height: 100px;
      position: absolute;
      right: 40px;
    }

    .content {
      width: 100%;
    }
  `,
};
